import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@library/components/sections/Accordion';
import { ActionButton, CloseModalButton } from '@library/components/buttons';
import {
  PlayerPackDetailsHeader,
  PlayerPackPicks,
  PlayerPacksFormFields
} from '@bo-components/playerPacks';
import {
  PlayerPackFormSchema,
  type PlayerPackFormValues
} from '@library/types/playerPacks/PlayerPackFormSchema';
import { useCallback, useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import type { ApiError } from '@bo-types/authentication';
import { DEFAULT_TRADING_FILTERS } from '@bo-constants/filters';
import { DateTime } from 'luxon';
import { Form } from '@library/components/forms/Form';
import type { GamePlayerProjectionsRequest } from '@library/types/projection';
import { GlobalFilters } from '@library/components/GlobalFilters';
import { LeagueDependentFilters } from '@library/components/LeagueDependentFilters';
import { Loading } from '@library/components/Loading';
import { Modal } from '@library/components/modals/Modal';
import { PATHS } from '@bo-constants/routes';
import { PageBody } from '@library/components/layout/PageBody';
import type { PlayerPackProjection } from '@library/types/playerPacks/playerPacks';
import type { PlayerPacksFilters } from '@library/types/filters';
import { Spinner } from '@library/components/Spinner';
import { getParameterValue } from '@library/component-utils/filters';
import { hasStandAloneOwnAttributes } from '@bo-utils/leagueAttributes';
import { removeDuplicatesInArray } from '@bo-utils/common';
import { selectUser } from '@bo-services/authenticationSlice';
import { useDashBoard } from '@bo-hooks/useDashboard';
import { useNavigate } from 'react-router-dom';
import { usePlayerPacks } from '@bo-hooks/playerPacks/usePlayerPacks';
import { usePlayerPacksExcludedStates } from '@bo-hooks/playerPacks/usePlayerPacksExcludedStates';
import { useSelector } from 'react-redux';
import { zodResolver } from '@hookform/resolvers/zod';

export const PlayerPackCreate = () => {
  const {
    changeFilterValue,
    durations,
    filters,
    fromDate,
    handleMultiSelectDurations,
    handleSearch,
    handleSelectDateRange,
    handleSelectLeague,
    isLeagueLoading,
    leagueInfo,
    leaguesList,
    searchValue,
    selectedDurations,
    selectedEndDate,
    selectedLeaguesDurationAttributes,
    selectedStartDate,
    setFilters,
    toDate
  } = useDashBoard({
    dashboardName: 'PLAYER_PACKS',
    initialStartDate: DateTime.now().startOf('day'),
    initialEndDate: DateTime.now().endOf('day')
  });

  const navigate = useNavigate();
  const loggedInUser = useSelector(selectUser);
  const { id: baseLeagueId, name } = leagueInfo ?? {};
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [pickedProjections, setPickedProjections] = useState<
    Array<PlayerPackProjection>
  >([]);

  const {
    demonsAndGoblins: selectedDemonsAndGoblins,
    games: selectedGames,
    statType: selectedStatType,
    teams: selectedTeams
  } = filters as PlayerPacksFilters;

  const allStats = useMemo(() => {
    const statTypes = selectedLeaguesDurationAttributes
      .map((leagueDurationAttributes) => leagueDurationAttributes.statTypes)
      .flat();

    return removeDuplicatesInArray(statTypes);
  }, [selectedLeaguesDurationAttributes]);

  const standAloneAttributes = useMemo(() => {
    return hasStandAloneOwnAttributes({ name });
  }, [name]);

  const leagueId = useMemo(
    () =>
      standAloneAttributes
        ? [baseLeagueId ?? '']
        : selectedLeaguesDurationAttributes.map(
            (leagueDurationAttributes) => leagueDurationAttributes.leagueId
          ),
    [baseLeagueId, selectedLeaguesDurationAttributes, standAloneAttributes]
  );

  const projectionsQueryParams = useMemo<GamePlayerProjectionsRequest>(() => {
    return {
      demonsAndGoblins: getParameterValue(selectedDemonsAndGoblins),
      durationId: standAloneAttributes ? selectedDurations : [],
      leagueId,
      fromDate,
      gameModeIds: [1], // Hardcoding this for pickem
      gameId: getParameterValue(selectedGames),
      search: searchValue,
      statTypeIds: getParameterValue(selectedStatType),
      team: getParameterValue(selectedTeams),
      toDate
    };
  }, [
    fromDate,
    leagueId,
    searchValue,
    selectedDemonsAndGoblins,
    selectedDurations,
    selectedGames,
    selectedStatType,
    selectedTeams,
    standAloneAttributes,
    toDate
  ]);

  const { projectionsForPicks, isLoadingProjections, games, createPlayerPack } =
    usePlayerPacks(projectionsQueryParams);

  const { allowedStates } = usePlayerPacksExcludedStates();

  const handleDemonsAndGoblinsChange = useCallback(
    (value: string) => {
      changeFilterValue('demonsAndGoblins', value);
    },
    [changeFilterValue]
  );

  const handleGamesChange = useCallback(
    (value: Array<string>) => {
      changeFilterValue('games', value);
    },
    [changeFilterValue]
  );

  const handleStatTypeChange = useCallback(
    (value: Array<string>) => {
      changeFilterValue('statType', value);
    },
    [changeFilterValue]
  );

  const handleTeamsChange = useCallback(
    (value: Array<string>) => {
      changeFilterValue('teams', value);
    },
    [changeFilterValue]
  );

  const handleReset = useCallback(() => {
    setFilters(DEFAULT_TRADING_FILTERS);
    handleSearch('');
  }, [setFilters, handleSearch]);

  const form = useForm<PlayerPackFormValues>({
    mode: 'all',
    resolver: zodResolver(PlayerPackFormSchema),
    defaultValues: {
      title: '',
      image: undefined,
      placeByTime: new Date().toISOString(),
      canFade: false,
      excludedStates: [],
      picks: pickedProjections.map((p) => ({
        wagerType: '',
        projectionId: p.projectionId
      }))
    }
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid }
  } = form;
  const fieldArrayForm = useFieldArray({
    control,
    name: 'picks'
  });

  const isFormValid = isDirty && isValid;

  const onSubmit = useCallback(
    async (formValue: PlayerPackFormValues) => {
      const picksFormatted = pickedProjections.map((proj, index) => ({
        line_score: proj.projectionValue,
        wager_type: formValue.picks.find(
          (p) => p.projectionId === proj.projectionId
        )?.wagerType,
        prediction_source: '',
        projection_id: proj.projectionId,
        rank: index + 1
      }));
      const playerPackFormData: FormData = new FormData();
      playerPackFormData.append('title', formValue.title);
      playerPackFormData.append('enabled', 'false');
      playerPackFormData.append('place_by', formValue.placeByTime);
      playerPackFormData.append('img', formValue.image);
      playerPackFormData.append('creator_id', loggedInUser?.id || '');
      playerPackFormData.append('creator_type', loggedInUser?.type || '');
      playerPackFormData.append(
        'can_fade',
        formValue.canFade ? 'true' : 'false'
      );
      playerPackFormData.append(
        'excluded_state_ids',
        formValue.excludedStates && formValue.excludedStates.length > 0
          ? formValue.excludedStates.join(',')
          : ''
      );
      playerPackFormData.append('picks', JSON.stringify(picksFormatted));
      try {
        await createPlayerPack(playerPackFormData).unwrap();
        setConfirmationMessage('Player Pack created!');
      } catch (resp) {
        const errorMessage =
          (resp as ApiError)?.data?.error ?? 'An unknown error occurred.';
        setConfirmationMessage(
          `There was an issue creating Player Pack: ${errorMessage}`
        );
      }
      setIsConfirmationModalOpen(true);
    },
    [createPlayerPack, pickedProjections, loggedInUser]
  );

  const handleConfirmationModalClose = useCallback(() => {
    setIsConfirmationModalOpen(false);
    navigate(PATHS.PLAYER_PACKS);
  }, [navigate]);

  if (isLeagueLoading) {
    return <Loading loadingMessage="Loading Leagues" />;
  }

  return (
    <>
      <PlayerPackDetailsHeader title="New Player Pack">
        <ActionButton
          handleOnClick={handleSubmit(onSubmit)}
          disabled={!isFormValid}
          title="Save"
        >
          Save
        </ActionButton>
      </PlayerPackDetailsHeader>
      <PageBody className="flex flex-col gap-4">
        <Form form={form} className="flex size-full flex-wrap gap-4 py-2">
          <Accordion
            type="multiple"
            defaultValue={['pack-information']}
            className="flex w-full flex-col gap-4"
          >
            <AccordionItem
              value="pack-information"
              className="border-soFresh-140 bg-soFresh-110 flex flex-1 flex-col gap-4 rounded-md border p-4"
            >
              <AccordionTrigger chevronPosition="right" className="text-base">
                Pack Information
              </AccordionTrigger>
              <AccordionContent>
                <PlayerPacksFormFields
                  formMethods={form}
                  isReadOnly={false}
                  allowedStates={allowedStates}
                />
              </AccordionContent>
            </AccordionItem>
            <AccordionItem
              value="lineup-configuration"
              className="border-soFresh-140 bg-soFresh-110 flex flex-1 flex-col gap-4 rounded-md border p-4"
            >
              <AccordionTrigger chevronPosition="right" className="text-base">
                Lineup Configuration
              </AccordionTrigger>
              <AccordionContent className="flex min-h-0 flex-col gap-4">
                <GlobalFilters
                  className="flex w-full gap-4"
                  durations={durations}
                  dateRange={false}
                  handleMultiSelectDurations={handleMultiSelectDurations}
                  handleSearch={handleSearch}
                  handleSelectDateRange={handleSelectDateRange}
                  handleSelectLeague={handleSelectLeague}
                  leaguesList={leaguesList}
                  searchValue={searchValue}
                  searchPlaceholder="Search"
                  selectedStartDate={selectedStartDate}
                  selectedEndDate={selectedEndDate}
                  selectedDurations={selectedDurations}
                  selectedLeagueId={baseLeagueId}
                />
                <LeagueDependentFilters
                  className="flex flex-wrap items-center gap-4"
                  filters={filters}
                  games={games}
                  handleDemonsAndGoblinsChange={handleDemonsAndGoblinsChange}
                  handleGamesChange={handleGamesChange}
                  handleStatTypeChange={handleStatTypeChange}
                  handleTeamsChange={handleTeamsChange}
                  onReset={handleReset}
                  statTypeValues={allStats}
                />
                <PlayerPackPicks
                  pickedProjections={pickedProjections}
                  setPickedProjections={setPickedProjections}
                  projections={projectionsForPicks}
                  isProjectionsLoading={isLoadingProjections}
                  formMethods={form}
                  fieldMethods={fieldArrayForm}
                />
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </Form>
      </PageBody>
      {isConfirmationModalOpen && (
        <Modal
          isOpen={isConfirmationModalOpen}
          onCancelModal={handleConfirmationModalClose}
        >
          <div className="bg-soFresh-110 border-soFresh-140 relative mx-auto w-[420px] flex-col rounded-md border">
            <div className="border-soFresh-140 flex h-16 items-center justify-between rounded-t border-b border-solid py-4 pl-6 pr-1">
              <p>Creating Player Pack</p>
              <CloseModalButton handleOnClick={handleConfirmationModalClose} />
            </div>
            <div className="text-soClean-180 flex flex-col p-6 text-sm">
              {confirmationMessage || <Spinner className="mr-1 mt-2.5" />}
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};
